import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useClinicConfigContext } from '@/contexts/ClinicConfigContext';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { usePrefetchClinicData } from '@/hooks/useClinicData';
import { useOptimizedNavigation } from '@/hooks/useOptimizedNavigation';
import { usePerformanceMonitor } from '@/hooks/usePerformanceMonitor';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { BookingApi } from '@/utils/api';

export function WelcomePage() {
  const { clinicSlug } = useParams<{ clinicSlug: string }>();
  const {
    clinic,
    clinicLoading,
    clinicError,
    config,
    configLoading,
    configError,
    services,
    servicesLoading,
    servicesError,
    refetchClinic,
    refetchConfig,
    refetchServices,
  } = useClinicConfigContext();

  // Prefetch services and testimonials for faster navigation
  const { prefetchServices, prefetchTestimonials } = usePrefetchClinicData();

  // Optimized navigation with prefetching
  const { navigateWithPrefetch, handleMouseEnter } = useOptimizedNavigation();

  // Performance monitoring
  const { trackUserInteraction } = usePerformanceMonitor('WelcomePage');

  // Fire counter on hard reload only, before other effects
  useEffect(() => {
    const navEntries = (performance?.getEntriesByType?.('navigation') ??
      []) as PerformanceNavigationTiming[];
    const isReload =
      navEntries.length > 0
        ? navEntries[0].type === 'reload'
        : typeof performance !== 'undefined' &&
          (performance as unknown as { navigation?: { type?: number } })
            .navigation?.type === 1;

    if (!isReload) return;

    const getLocationId = (): number | string | undefined => {
      try {
        const raw = localStorage.getItem('clinic_config');
        if (!raw) return undefined;
        const parsed = JSON.parse(raw) as {
          location?: { id?: number | string };
        };
        return parsed?.location?.id;
      } catch {
        return undefined;
      }
    };

    const locationId = getLocationId();
    if (locationId) {
      void BookingApi.hitCounter(locationId).catch(() => {
        // ignore errors
      });
    }
  }, []);

  // Fetch clinic data, config, and services if not already loaded
  useEffect(() => {
    if (!clinic && !clinicLoading && clinicSlug) {
      refetchClinic();
    }
    if (!config && !configLoading && clinicSlug) {
      refetchConfig();
    }
    if (!services && !servicesLoading && clinicSlug) {
      refetchServices();
    }
  }, [
    clinic,
    clinicLoading,
    clinicSlug,
    refetchClinic,
    config,
    configLoading,
    refetchConfig,
    services,
    servicesLoading,
    refetchServices,
  ]);

  // Prefetch services and testimonials for faster navigation to subsequent pages
  useEffect(() => {
    if (clinicSlug) {
      // Prefetch services and testimonials in the background
      prefetchServices(clinicSlug);
      prefetchTestimonials(clinicSlug);
    }
  }, [clinicSlug, prefetchServices, prefetchTestimonials]);

  // Theme is initialized globally via ThemeInitializer (see App.tsx)

  // Document title
  useDocumentTitle(clinic?.name);

  // Fire counter exactly once per hard load (not on back/forward navigation)
  useEffect(() => {
    // Only run on first page load (skip back/forward navigation)
    const navEntries = (performance?.getEntriesByType?.('navigation') ??
      []) as PerformanceNavigationTiming[];
    const isNavigate =
      navEntries.length > 0 ? navEntries[0].type === 'navigate' : true;

    const locationId = config?.location?.id;
    if (isNavigate && locationId) {
      void BookingApi.hitCounter(locationId).catch(() => {
        // Silently ignore
      });
    }
  }, [config?.location?.id]);

  const isLoading = clinicLoading || configLoading || servicesLoading;

  if (clinicError || configError || servicesError || !clinic) {
    return (
      <div className='min-h-screen relative flex items-center justify-center'>
        <div className='text-center'>
          <h1 className='text-4xl font-bold text-gray-800 mb-4'>404</h1>
          <p className='text-xl text-gray-600 mb-8'>Clinic not found</p>
        </div>
      </div>
    );
  }

  // Get config data
  const heading =
    config?.active_theme?.effective_heading || 'Smarter Dental Care is Here';
  const subtext =
    config?.active_theme?.effective_subtext ||
    'Experience the future of dentistry in NYC with our AI-powered booking and seamless patient experience.';

  // Resolve hero image URL from config.active_theme.selected_image (and fallbacks)
  const resolveImageUrl = (raw: unknown): string | undefined => {
    if (!raw) return undefined;
    if (typeof raw === 'string') {
      return raw.startsWith('http') ? raw : `https://core-api.elva.ai${raw}`;
    }
    if (typeof raw === 'object') {
      const obj = raw as Record<string, unknown>;
      const directCandidate = (obj.url || obj.src || obj.path) as
        | string
        | undefined;
      if (directCandidate && typeof directCandidate === 'string') {
        return directCandidate.startsWith('http')
          ? directCandidate
          : `https://core-api.elva.ai${directCandidate}`;
      }
      const imageField = obj.image as unknown;
      if (typeof imageField === 'string') {
        return imageField.startsWith('http')
          ? imageField
          : `https://core-api.elva.ai${imageField}`;
      }
      if (typeof imageField === 'object' && imageField) {
        const nested = imageField as Record<string, unknown>;
        const nestedCandidate = (nested.url || nested.src || nested.path) as
          | string
          | undefined;
        if (nestedCandidate && typeof nestedCandidate === 'string') {
          return nestedCandidate.startsWith('http')
            ? nestedCandidate
            : `https://core-api.elva.ai${nestedCandidate}`;
        }
      }
    }
    return undefined;
  };

  const themeImages = (
    config?.active_theme?.theme as { images?: unknown[] } | undefined
  )?.images;
  const heroImageUrl =
    resolveImageUrl(config?.active_theme?.selected_image) ||
    // Fallbacks: common fields present in some themes
    resolveImageUrl(themeImages?.[0]) ||
    resolveImageUrl(config?.active_theme?.image_1);

  // Get layout settings from theme
  const layoutSettings = config?.active_theme?.theme?.layout_settings;
  const heightFull = layoutSettings?.height === 'full';
  const flexDirection = layoutSettings?.['flex-direction'] || 'row';
  const isRowReverse = flexDirection === 'row-reverse';

  // Determine container classes based on layout settings
  const containerClasses = heightFull
    ? 'h-screen w-screen overflow-hidden relative flex flex-col'
    : 'h-screen w-screen overflow-hidden relative flex';

  const contentClasses = heightFull
    ? 'flex-1 flex flex-col justify-center items-center p-4 sm:p-6 lg:p-8'
    : 'flex-1 flex flex-col justify-center items-center p-4 sm:p-6 lg:p-8 max-h-screen';

  // Determine image position based on flex-direction and height settings
  const imageClasses = heightFull
    ? isRowReverse
      ? 'absolute top-0 left-0 h-full w-auto object-cover select-none pointer-events-none'
      : 'absolute top-0 right-0 h-full w-auto object-cover select-none pointer-events-none'
    : isRowReverse
      ? 'absolute bottom-0 left-0 object-contain select-none pointer-events-none'
      : 'absolute bottom-0 right-0 object-contain select-none pointer-events-none';

  return (
    <div className={containerClasses}>
      {isLoading && (
        <LoadingSpinner
          fullScreen
          size='lg'
          text='Loading clinic information...'
        />
      )}
      {/* Content Section */}
      <div className={contentClasses}>
        {/* Header */}
        <div>
          {/* Hero Section */}
          <div className='mb-8 text-center items-center'>
            <span
              className='text-center lg:text-3xl text-xl font-semibold'
              style={{ color: 'var(--clinic-text)' }}
            >
              Online Booking
            </span>
            <h2
              className='text-5xl lg:text-6xl lg:max-w-[600px] font-semibold mb-6 leading-tight text-center mx-auto'
              style={{ color: 'var(--clinic-text)' }}
            >
              {heading}
            </h2>
            <p
              className='text-xl text-center font-normal mb-8 max-w-md mx-auto'
              style={{ color: 'var(--clinic-text)' }}
            >
              {subtext}
            </p>
          </div>

          {/* CTA Button */}
          <div className='mb-8 flex flex-col items-center'>
            <button
              onClick={() => {
                trackUserInteraction('book_appointment_clicked');
                navigateWithPrefetch(
                  `/${clinicSlug}/patient-visit`,
                  clinicSlug || '',
                  'all'
                );
              }}
              onMouseEnter={() => {
                if (clinicSlug) {
                  handleMouseEnter(clinicSlug, 'all');
                }
              }}
              className='text-white px-9 py-2.5 rounded-full font-semibold lg:text-lg text-base flex items-center justify-center transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl'
              style={{
                background: 'var(--clinic-secondary)',
              }}
            >
              <span>Book Now</span>
            </button>
          </div>
        </div>
      </div>
      {heroImageUrl && (
        <img
          src={heroImageUrl}
          alt='Clinic visual'
          aria-hidden='true'
          loading='lazy'
          className={imageClasses}
          style={{
            maxHeight: heightFull ? '100vh' : '70vh',
            maxWidth: heightFull ? '50vw' : '45vw',
            filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.15))',
          }}
        />
      )}
    </div>
  );
}
